
import React from 'react';
import { useMapApiKey } from './map/useMapApiKey';
import { useMapSizes } from './map/useMapSizes';
import { MapErrorState } from './map/MapErrorState';
import { MapLoadingState } from './map/MapLoadingState';
import { SingleLocationMap } from './map/SingleLocationMap';
import { MultiLocationMap } from './map/MultiLocationMap';

interface GoogleMapProps {
  address: string;
  size?: 'small' | 'medium' | 'large' | 'extra-large' | 'full';
  showCard?: boolean;
  className?: string;
  apiKeyOverride?: string;
  nursingHomes?: any[];
  countySlug?: string;
  defaultExpanded?: boolean;
  allNursingHomes?: boolean;
  latitude?: number;
  longitude?: number;
}

export function GoogleMap({
  address,
  size = 'medium',
  showCard = true,
  className = '',
  apiKeyOverride,
  nursingHomes,
  countySlug,
  defaultExpanded = true,
  allNursingHomes = false,
  latitude,
  longitude
}: GoogleMapProps) {
  const { apiKey, isLoading, hasError, setHasError } = useMapApiKey(apiKeyOverride);
  const { width, height } = useMapSizes(size);

  // If there's an error or no API key, display the error state
  if (hasError || !apiKey || !address) {
    return <MapErrorState address={address} showCard={showCard} className={className} />;
  }

  // If data is still loading, show the loading state
  if (isLoading) {
    return <MapLoadingState width={width} height={height} />;
  }

  // Choose the map type based on whether we have multiple nursing homes
  if (!nursingHomes || nursingHomes.length === 0) {
    // For single location (nursing home detail page)
    return (
      <SingleLocationMap
        address={address}
        apiKey={apiKey}
        isLoading={isLoading}
        setHasError={setHasError}
        width={width}
        height={height}
        showCard={showCard}
        className={className}
        latitude={latitude}
        longitude={longitude}
      />
    );
  } else {
    // For multiple locations (county page or all nursing homes)
    return (
      <MultiLocationMap
        address={address}
        apiKey={apiKey}
        nursingHomes={nursingHomes}
        countySlug={countySlug}
        setHasError={setHasError}
        width={width}
        height={height}
        className={className}
        defaultExpanded={defaultExpanded}
        allNursingHomes={allNursingHomes}
      />
    );
  }
}
